import { Box } from '@mui/material';
import { Column } from 'material-table';
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useEffect, useState, useMemo } from 'react';
import { IDriverEarningTypes } from 'common/src/types';
import { CustomMaterialTable } from 'components/CustomMaterialTable';
import { fetchDriverEarnings } from 'common/src/actions/driverearningaction';
import { fetchEarningsReportProps } from 'common/src/actions/earningreportsaction';
import { AutocompleteOption, ThemedAutocomplete } from 'components/input/autocomplete/ThemedAutocomplete';

interface DriverEarningProps {
  fleetAdminId?: string;
  fleetAdminName?: string;
  period?: fetchEarningsReportProps['period']
}

export default function DriverEarning({ fleetAdminId, fleetAdminName, period = 'monthly' }: DriverEarningProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedDriver, setSelectedDriver] = useState<string>('all');
  const driverearningdata = useSelector((state) => state.driverearningdata);
  const [selectedDriverObj, setSelectedDriverObj] = useState<AutocompleteOption | null>(null);


  const driverOptions: AutocompleteOption[] = useMemo(() => {
    if (!driverearningdata.driverearnings) return [];
    const driversMap = new Map();
    driverearningdata.driverearnings.forEach((item: any) => {
      if (!driversMap.has(item.driverUId)) {
        driversMap.set(item.driverUId, {
          id: item.driverUId,
          name: item.driverName,
          vehicle: item.driverVehicleNo
        });
      }
    });

    const drivers = Array.from(driversMap.values());

    const allOption: AutocompleteOption[] = [
      { label: t('all_drivers'), value: 'all', icon: 'Groups' }
    ];

    const driverOptionsList: AutocompleteOption[] = drivers.map((driver: any) => ({
      label: `${driver.name} - ${driver.vehicle}`,
      value: driver.id,
      icon: 'Person'
    }));

    return [...allOption, ...driverOptionsList];
  }, [driverearningdata.driverearnings, t]);

  useEffect(() => {
    if (selectedDriver === 'all') {
      setSelectedDriverObj({ label: t('all_drivers'), value: 'all' });
    } else {
      const found = driverOptions.find(opt => opt.value === selectedDriver);
      if (found) {
        setSelectedDriverObj(found);
      }
    }
  }, [selectedDriver, driverOptions, t]);

  useEffect(() => {
    const driverId = selectedDriver === 'all' ? undefined : selectedDriver;
    dispatch(fetchDriverEarnings({ fleetAdminId: fleetAdminId || null, period, selectedDriverId: driverId, t }));
  }, [fleetAdminId, period, selectedDriver, dispatch]);

  const columns: Column<IDriverEarningTypes>[] = [
    { title: t('driver_name'), field: 'driverName' },
    { title: t('booking_count'), field: 'total_rides' },
    { title: t('earning_amount'), field: 'driverShare' },
    { title: t('vehicle_reg_no'), field: 'driverVehicleNo' },
    { title: period === 'daily' ? t('date') : period === 'weekly' ? t('week') : period === 'yearly' ? t('year') : t('months'), field: 'displayLabel' },
  ];

  const handleDriverChange = (value: AutocompleteOption | AutocompleteOption[] | string | string[] | null) => {
    if (value && typeof value === 'object' && 'value' in value) {
      const selectedValue = (value as AutocompleteOption).value;
      setSelectedDriver(selectedValue);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>

        <ThemedAutocomplete
          showIcons={true}
          returnMode="object"
          heightContent="auto"
          options={driverOptions}
          value={selectedDriverObj}
          onChange={handleDriverChange}
          placeholder={t('select_driver')}
        />

      </Box>

      <CustomMaterialTable
        columns={columns}
        isLoading={driverearningdata.loading}
        data={driverearningdata.driverearnings ?? []}
        title={(fleetAdminId ? `${t('driver_earning')} - ${fleetAdminName}` : t('driver_earning')) as any}
      />
    </Box>
  );
}